import {
  OrgMetric,
  OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric,
  OrgWhatsAppTemplateCategoryMetric,
} from 'modules/domains/billing';
import { MetricCell, TableMetric } from '../AnalyticTable';

/** Top-line metrics */
export const createWhatsAppMetricsByName = (
  metrics: OrgWhatsAppTemplateCategoryMetric[],
): Record<string, OrgMetric> =>
  metrics.reduce((acc, curr) => {
    if (curr.templateCategory === 'Utility') {
      return {
        ...acc,
        whatsAppUtility: curr,
      };
    }

    if (curr.templateCategory === 'Marketing') {
      return {
        ...acc,
        whatsAppMarketing: curr,
      };
    }
    return acc;
  }, {});

type CountryMetric = { countryName: string; marketing: number; total: number; utility: number };

/** Country breakdown table */
export const createCountryMetrics = (
  metrics: OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric[],
): CountryMetric[] => {
  const reduced = metrics.reduce(
    (acc, curr) => {
      const { recipientCountryName, templateCategory, value } = curr;
      const isUtilityMetric = templateCategory === 'Utility';
      const isMarketingMetric = templateCategory === 'Marketing';
      const currentMarketingValue = isMarketingMetric ? value : 0;
      const currentUtilityValue = isUtilityMetric ? value : 0;
      const countryMarketingValue = acc[recipientCountryName]?.marketing || 0;
      const countryUtilityValue = acc[recipientCountryName]?.utility || 0;
      const marketingValue = countryMarketingValue + currentMarketingValue;
      const utilityValue = countryUtilityValue + currentUtilityValue;

      return {
        ...acc,
        [recipientCountryName]: {
          countryName: recipientCountryName,
          marketing: marketingValue,
          total: marketingValue + utilityValue,
          utility: utilityValue,
        },
      };
    },
    {} as Record<string, CountryMetric>,
  );

  const sorted = Object.values(reduced).sort((a, b) => b.total - a.total);

  return sorted;
};

export const sumAllCountries = (metrics: CountryMetric[]): MetricCell[] =>
  metrics.reduce(
    (acc, curr) => {
      const { marketing, utility } = curr;
      return [
        { isTotal: true, value: acc[0].value + utility },
        { isTotal: true, value: acc[1].value + marketing },
      ];
    },
    [{ value: 0 }, { value: 0 }],
  );

export const createTableMetrics = (metrics: {
  [orgProjectId: string]: OrgProjectWhatsAppTemplateCategoryRecipientCountryMetric[];
}): TableMetric[] => {
  const combinedMetrics = Object.values(metrics).flat();

  const countryMetrics = createCountryMetrics(combinedMetrics);
  const maxRows = 5;
  const topMetrics = countryMetrics.slice(0, maxRows);
  const restMetrics = countryMetrics.slice(maxRows);

  const tableMetrics = topMetrics.map((metric) => ({
    metricName: { label: metric.countryName },
    // values provided in same order as the columns
    values: [{ value: metric.utility }, { value: metric.marketing }],
  }));

  const tableMetricsWithSum =
    restMetrics.length === 0
      ? tableMetrics
      : [
          ...tableMetrics,
          {
            metricName: { label: 'All other countries' },
            values: sumAllCountries(restMetrics),
          },
        ];

  return tableMetricsWithSum;
};
